import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { BRAND } from "../Brand";
import { X, Plus } from "@phosphor-icons/react";

const WEEKDAYS = [
  { i: 0, label: "Mon" },
  { i: 1, label: "Tue" },
  { i: 2, label: "Wed" },
  { i: 3, label: "Thu" },
  { i: 4, label: "Fri" },
  { i: 5, label: "Sat" },
  { i: 6, label: "Sun" },
];

export default function CalendarDialog({ open, onOpenChange, projectId, canEdit, onSaved }) {
  const [weekendDays, setWeekendDays] = useState([5, 6]);
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    api.get("/plan/calendar", { params: { project_id: projectId } })
      .then((r) => {
        setWeekendDays(r.data.weekend_days || [5, 6]);
        setHolidays(r.data.holidays || []);
      })
      .catch(() => {
        setWeekendDays([5, 6]); setHolidays([]);
      })
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const toggleWeekend = (i) => {
    setWeekendDays((cur) => cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i].sort((a, b) => a - b));
  };

  const addHoliday = () => {
    if (!newHoliday) return;
    const iso = newHoliday.slice(0, 10);
    if (holidays.includes(iso)) return;
    setHolidays([...holidays, iso].sort());
    setNewHoliday("");
  };

  const removeHoliday = (iso) => {
    setHolidays(holidays.filter((h) => h !== iso));
  };

  const save = async () => {
    try {
      await api.put("/plan/calendar", {
        project_id: projectId,
        weekend_days: weekendDays,
        holidays,
      });
      toast.success("Working calendar saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="plan-calendar-dialog">
        <DialogHeader>
          <DialogTitle>Working-day calendar</DialogTitle>
          <DialogDescription>
            Configure non-working days for this project. The CPM engine skips these
            when computing task starts and finishes.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-neutral-500 py-4">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Weekend days</label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => {
                  const active = weekendDays.includes(d.i);
                  return (
                    <button
                      type="button"
                      key={d.i}
                      data-testid={`plan-calendar-day-${d.i}`}
                      onClick={() => canEdit && toggleWeekend(d.i)}
                      disabled={!canEdit}
                      className={`h-9 min-w-[52px] rounded-md border text-xs font-semibold uppercase tracking-widest transition-colors ${
                        active
                          ? "bg-neutral-900 border-neutral-900 text-white"
                          : "border-neutral-200 text-neutral-600 hover:text-neutral-900"
                      } ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[11px] text-neutral-500">Tap to mark a day as non-working.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Holidays</label>
              {canEdit ? (
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="date"
                    value={newHoliday}
                    onChange={(e) => setNewHoliday(e.target.value)}
                    data-testid="plan-calendar-holiday-input"
                    className="h-9 max-w-[200px]"
                  />
                  <Button type="button" size="sm" variant="outline" onClick={addHoliday} data-testid="plan-calendar-add-holiday">
                    <Plus size={12} weight="bold" className="mr-1" /> Add
                  </Button>
                </div>
              ) : null}
              <div className="mt-2 max-h-40 overflow-y-auto border border-neutral-200 rounded p-2 flex flex-wrap gap-1.5">
                {holidays.length === 0 ? (
                  <div className="text-xs text-neutral-400">No holidays yet.</div>
                ) : holidays.map((h) => (
                  <div key={h}
                       data-testid={`plan-calendar-holiday-${h}`}
                       className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded border border-neutral-200 bg-neutral-50 text-xs font-mono">
                    {h}
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => removeHoliday(h)}
                        data-testid={`plan-calendar-remove-${h}`}
                        className="h-5 w-5 flex items-center justify-center rounded text-neutral-400 hover:text-red-500 hover:bg-red-50"
                      >
                        <X size={10} weight="bold" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="plan-calendar-cancel">
            Cancel
          </Button>
          {canEdit ? (
            <Button
              type="button"
              onClick={save}
              className="text-white"
              style={{ backgroundColor: BRAND.colors.blue }}
              data-testid="plan-calendar-save"
            >
              Save & reschedule
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
